import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller, SubmitHandler } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Typography,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  Autocomplete,
  Breadcrumbs,
  Link,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { Oportunidade, OportunidadeEstagio } from '@/types/oportunidade'
import { OportunidadeRequest } from '@/types/oportunidade'
import { formatCurrency } from '@/utils/formatters'
import { CurrencyField } from '@/components/common/CurrencyField'
import { mockClientes } from '@/utils/mockData'

const oportunidadeFormSchema = z.object({
  clienteId: z.string().min(1, 'Cliente é obrigatório'),
  tipoNecessidade: z
    .string()
    .min(3, 'Tipo de Necessidade é obrigatório'),
  valorEstimado: z
    .number()
    .min(1000, 'Valor mínimo é R$ 1.000'),
  prazoDesejado: z
    .number()
    .min(1, 'Prazo é obrigatório')
    .max(360, 'Prazo máximo é 360 meses'),
  descricaoNecessidade: z
    .string()
    .min(10, 'Descrição deve ter pelo menos 10 caracteres'),
  faturamentoCliente: z
    .number()
    .min(0, 'Faturamento não pode ser negativo'),
  fluxoCaixa: z.string().optional(),
  historico: z.string().optional(),
})

type OportunidadeFormData = z.infer<typeof oportunidadeFormSchema>

interface LocationState {
  oportunidade?: Oportunidade
}

const OportunidadeFormPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { oportunidade } = (location.state as LocationState) || {}

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<OportunidadeFormData>({
    resolver: zodResolver(oportunidadeFormSchema),
    defaultValues: oportunidade
      ? {
          clienteId: oportunidade.clienteId,
          tipoNecessidade: oportunidade.tipoNecessidade,
          valorEstimado: oportunidade.valorEstimado,
          prazoDesejado: 12,
          descricaoNecessidade: oportunidade.descricao,
          faturamentoCliente: 0,
          fluxoCaixa: '',
          historico: '',
        }
      : {
          clienteId: '',
          tipoNecessidade: '',
          valorEstimado: 0,
          prazoDesejado: 12,
          descricaoNecessidade: '',
          faturamentoCliente: 0,
          fluxoCaixa: '',
          historico: '',
        },
  })

  const clienteId = watch('clienteId')
  const valorEstimado = watch('valorEstimado')
  const prazoDesejado = watch('prazoDesejado')

  const clienteSelecionado = mockClientes.find((c) => c.id === clienteId)

  const onSubmit: SubmitHandler<OportunidadeFormData> = async (data) => {
    try {
      setLoading(true)
      setError(null)

      const request: OportunidadeRequest = {
        clienteId: data.clienteId,
        tipoNecessidade: data.tipoNecessidade,
        valorEstimado: data.valorEstimado,
        descricao: data.descricaoNecessidade,
        estagio: 'DIAGNOSTICO' as OportunidadeEstagio,
      }

      console.log('Enviando oportunidade:', request)

      setTimeout(() => {
        setLoading(false)
        navigate('/oportunidades')
      }, 1000)
    } catch (err) {
      setError('Erro ao salvar oportunidade')
      setLoading(false)
    }
  }

  const tiposNecessidade = [
    'Capital de Giro',
    'Vendor Finance',
    'Risco Sacado',
    // P1 (AN-PP1 §4): nome novo do produto RECEIVABLES_ADVANCE.
    'Antecipação de Recebíveis',
    'Home Equity',
    'Consignado Privado',
    'Comissão com Incentivo',
    'Refinanciamento',
    'Reestruturação de Dívida',
  ]

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/oportunidades')}
          sx={{ cursor: 'pointer' }}
        >
          Oportunidades
        </Link>
        <Typography color="textPrimary">
          {oportunidade ? 'Editar Oportunidade' : 'Nova Oportunidade'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/oportunidades')}
        >
          Voltar
        </Button>
        <Typography variant="h4" fontWeight={600}>
          {oportunidade ? 'Editar Oportunidade' : 'Nova Oportunidade'}
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Seção: Dados Principais */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Dados Principais" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="clienteId"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={mockClientes}
                          getOptionLabel={(option) => option.razaoSocial}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={clienteSelecionado || null}
                          onChange={(_, value) =>
                            field.onChange(value?.id || '')
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Cliente"
                              size="small"
                              error={!!errors.clienteId}
                              helperText={errors.clienteId?.message}
                              required
                            />
                          )}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="tipoNecessidade"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Tipo de Necessidade</InputLabel>
                          <Select {...field} label="Tipo de Necessidade">
                            {tiposNecessidade.map((tipo) => (
                              <MenuItem key={tipo} value={tipo}>
                                {tipo}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                    {errors.tipoNecessidade && (
                      <Typography variant="caption" color="error">
                        {errors.tipoNecessidade.message}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="valorEstimado"
                      control={control}
                      render={({ field }) => (
                        <CurrencyField
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputRef={field.ref}
                          label="Valor Estimado"
                          fullWidth
                          size="small"
                          error={!!errors.valorEstimado}
                          helperText={errors.valorEstimado?.message}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="prazoDesejado"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Prazo Desejado"
                          fullWidth
                          size="small"
                          type="number"
                          inputProps={{ min: '1', max: '360' }}
                          error={!!errors.prazoDesejado}
                          helperText={errors.prazoDesejado?.message}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">meses</InputAdornment>
                            ),
                          }}
                          required
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="descricaoNecessidade"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descrição da Necessidade"
                          fullWidth
                          multiline
                          rows={4}
                          size="small"
                          error={!!errors.descricaoNecessidade}
                          helperText={errors.descricaoNecessidade?.message}
                          required
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Dados Financeiros */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Dados Financeiros" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="faturamentoCliente"
                      control={control}
                      render={({ field }) => (
                        <CurrencyField
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                          inputRef={field.ref}
                          label="Faturamento Anual do Cliente"
                          fullWidth
                          size="small"
                          error={!!errors.faturamentoCliente}
                          helperText={errors.faturamentoCliente?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Valor da Parcela (Estimado)
                      </Typography>
                      <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                        {valorEstimado && prazoDesejado
                          ? formatCurrency(valorEstimado / prazoDesejado)
                          : 'R$ 0,00'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="fluxoCaixa"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Análise de Fluxo de Caixa"
                          fullWidth
                          multiline
                          rows={3}
                          size="small"
                          placeholder="Descreva o fluxo de caixa do cliente..."
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Seção: Histórico */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Histórico e Observações" />
              <CardContent>
                <Controller
                  name="historico"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Histórico de Relacionamento"
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      placeholder="Adicione informações sobre o histórico com este cliente..."
                    />
                  )}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Botões */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/oportunidades')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                type="submit"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}

export default OportunidadeFormPage
